import { useCallback } from "react";
import validator from "validator";
import { sendUserData } from "@utils/api";
import { requirements } from "@components/PasswordRequirements";
import { useAccountCreationStore } from "../store/useAccountCreationStore";

interface UseAccountCreationOptions {
  userType: string;
  token?: string | null;
  businessSlug?: string;
  assigneeId?: string | null;
  referrerId?: string | null;
  redirect?: boolean;
  skipRedirect?: boolean; // Skip redirect even if token is present (for new onboarding flow)
  onSuccess?: (data: any) => void;
}

export const useAccountCreation = ({
  userType,
  token,
  businessSlug,
  assigneeId,
  referrerId,
  redirect = true,
  skipRedirect = false,
  onSuccess,
}: UseAccountCreationOptions) => {
  const {
    formData,
    error,
    setFormField,
    setError,
    setNotification,
    setLoading,
    setShowVerification,
    setAccountData,
  } = useAccountCreationStore();

  const handleEmailChange = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const enteredEmail = e.target.value.toLowerCase();
      setFormField("email", enteredEmail);

      if (!validator.isEmail(enteredEmail)) {
        setError({
          email: "Please enter a valid email address",
          password: "",
        });
      } else {
        setError({ email: "", password: "" });
      }
    },
    [setFormField, setError]
  );

  const allRequirementsMet =
    requirements.every((req) => req.re.test(formData.password)) &&
    formData.password.length > 5;

  const validateAndSubmit = useCallback(async () => {
    if (formData.password !== formData.reenteredPassword) {
      setError({ email: "", password: "Passwords do not match" });
      setLoading(false);
      setNotification(null);
      return;
    }

    setNotification({
      title: "Currently Creating Your Account",
      message: "Please wait while we set you up for success",
      color: "blue",
      loading: true,
    });
    setLoading(true);

    try {
      const response = await sendUserData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        userType,
        token,
        password: formData.password,
        businessId: businessSlug,
        assigneeId,
        ...(formData.userBusinessName && {
          businessName: formData.userBusinessName,
        }),
        ...(referrerId && {
          referrerId: referrerId,
        }),
      });

      if ("error" in response) {
        setNotification({
          title: "Error",
          message: response.message,
          color: "red",
          loading: false,
        });
      } else if (response?.status >= 200 && response?.status < 300) {
        // Store account data for later use
        setAccountData(response.data.content);

        // Skip redirect if explicitly requested (new onboarding flow)
        if (skipRedirect) {
          onSuccess?.(response.data.content);
        } else if (token) {
          // Old behavior: redirect team members to home
          window.location.href = `/`;
        } else if (redirect) {
          setShowVerification(true);
        } else {
          onSuccess?.(response.data.content);
        }
      } else {
        setNotification({
          title: "Error",
          message: "An unexpected error occurred.",
          color: "red",
          loading: false,
        });
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An unexpected error occurred.";
      setNotification({
        title: "Error",
        message: errorMessage,
        color: "red",
        loading: false,
      });
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    userType,
    token,
    businessSlug,
    assigneeId,
    referrerId,
    redirect,
    skipRedirect,
    setError,
    setNotification,
    setLoading,
    setShowVerification,
    setAccountData,
    onSuccess,
  ]);

  return {
    formData,
    error,
    handleEmailChange,
    validateAndSubmit,
    allRequirementsMet,
  };
};
